/** Client-side image compression: longest side ≤ maxSide, JPEG ~80%, HEIC → JPEG. */

const isHeic = (file) =>
  /image\/hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || "");

const loadBitmap = async (blob) => {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(blob, { imageOrientation: "from-image" });
    } catch {}
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.src = url;
  });
};

const heicToJpeg = async (file) => {
  // iOS Safari decodes HEIC natively; other browsers need heic2any (lazy-loaded).
  try {
    await loadBitmap(file);
    return file;
  } catch {}
  const { default: heic2any } = await import("heic2any");
  const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
  return Array.isArray(out) ? out[0] : out;
};

export async function compressImage(file, { maxSide = 1600, quality = 0.8, onProgress = () => {} } = {}) {
  onProgress(5);
  let source = file;
  if (isHeic(file)) source = await heicToJpeg(file);
  onProgress(30);
  const bmp = await loadBitmap(source);
  const w = bmp.width || bmp.naturalWidth;
  const h = bmp.height || bmp.naturalHeight;
  const scale = Math.min(1, maxSide / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close?.();
  onProgress(70);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  if (!blob) throw new Error("compress-failed");
  onProgress(100);
  const name = (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}

export const isImageFile = (file) => file.type.startsWith("image/") || isHeic(file);
export const isVideoFile = (file) => file.type.startsWith("video/") || /\.(mp4|mov|webm|3gp)$/i.test(file.name || "");
